import type { HttpQueryParam, IHttpClient } from "@/core/interfaces";
import { extractItems, isRecord, readFirstText, readText } from "@/core/utils";
import { appendReportQuery, parseContentDispositionFileName } from "@/modules/export";
import type { ExportedFile, ExportFormat } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import { BUSCA_MAX_LENGTH } from "./periodo";
import type {
  AlocacaoDaObra,
  AlocacaoDoFornecedor,
  FornecedorAlocacoes,
  FornecedorConflitoIgnorado,
  FornecedorResumo,
  ListAlocacoesParams,
  ObraAlocacoes,
  ObraEscopoMacro,
  ObraResumo,
  ObraTipo,
} from "./recursos.types";

export interface ExportAlocacoesFornecedorParams {
  dataInicio: string;
  dataFim: string;
  busca?: string;
  /** Rótulos dos filtros da tela, impressos no arquivo. A busca já restringe os dados. */
  filtros?: string[];
  format: ExportFormat;
  signal?: AbortSignal;
}

/**
 * Recursos do contrato da US-1774, expostos pelo hub-bff sob `/api`, como
 * `/api/embarcacoes`. A resposta vem no envelope `{ success, message, data: [...] }`.
 */
export const RECURSOS_PATHS = {
  ALOCACOES_POR_FORNECEDOR: "/api/recursos/alocacoes/fornecedores",
  ALOCACOES_POR_OBRA: "/api/recursos/alocacoes/obras/fornecedores",
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function readId(raw: Record<string, unknown>): string {
  const value = raw.id;
  if (typeof value === "number") return String(value);
  return readText(value).trim();
}

/** `codigo` pode chegar numérico (ex.: 97). */
function readCodigo(raw: Record<string, unknown>): string {
  const value = raw.codigo;
  if (typeof value === "number") return String(value);
  return readText(value).trim();
}

/** `YYYY-MM-DD`; tolera um carimbo ISO completo, ficando só com a data. */
function readDate(raw: Record<string, unknown>, key: string): string {
  return readText(raw[key]).trim().slice(0, 10);
}

function toFornecedorResumo(raw: unknown): FornecedorResumo {
  const record = isRecord(raw) ? raw : {};
  return {
    id: readId(record),
    codigo: readCodigo(record),
    nome: readFirstText(record, "nome").trim(),
  };
}

function readList(raw: Record<string, unknown>, key: string): Record<string, unknown>[] {
  const value = raw[key];
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function toObraTipos(raw: Record<string, unknown>): ObraTipo[] {
  return readList(raw, "tipos").map((tipo) => ({
    id: readId(tipo),
    codigo: readCodigo(tipo),
    tipo: readText(tipo.tipo).trim(),
  }));
}

function toObraEscoposMacro(raw: Record<string, unknown>): ObraEscopoMacro[] {
  return readList(raw, "escoposMacro").map((escopo) => ({
    id: readId(escopo),
    escopo: readText(escopo.escopo).trim(),
    inicio: readDate(escopo, "inicio"),
    fim: readDate(escopo, "fim"),
    tipoObraId:
      typeof escopo.tipoObraId === "number"
        ? String(escopo.tipoObraId)
        : readText(escopo.tipoObraId).trim(),
  }));
}

function toObraResumo(raw: unknown): ObraResumo {
  const record = isRecord(raw) ? raw : {};
  const obraInicio = readDate(record, "obraInicio");
  const obraFim = readDate(record, "obraFim");
  return {
    ...(obraInicio && obraFim ? { obraInicio, obraFim } : {}),
    tipos: toObraTipos(record),
    escoposMacro: toObraEscoposMacro(record),
    id: readId(record),
    codigo: readCodigo(record),
    nome: readFirstText(record, "nome").trim(),
    situacao: readFirstText(record, "situacao").trim(),
    embarcacao: isRecord(record.embarcacao)
      ? {
          id: readFirstText(record.embarcacao, "id").trim(),
          nome: readFirstText(record.embarcacao, "nome").trim(),
          acronimo: readFirstText(record.embarcacao, "acronimo").trim() || null,
        }
      : null,
  };
}

function toConflitoIgnorado(raw: Record<string, unknown>): FornecedorConflitoIgnorado | null {
  const conflitoInicio = readDate(raw, "conflitoInicio");
  const conflitoFim = readDate(raw, "conflitoFim");
  if (!conflitoInicio || !conflitoFim) return null;
  const ids: unknown[] = Array.isArray(raw.alocacaoIds) ? raw.alocacaoIds : [];
  return {
    id: readId(raw),
    fornecedorId: readText(raw.fornecedorId).trim(),
    conflitoInicio,
    conflitoFim,
    alocacaoIds: ids
      .map((value) => (typeof value === "number" ? String(value) : readText(value).trim()))
      .filter(Boolean),
  };
}

function readAlocacoes(raw: Record<string, unknown>): Record<string, unknown>[] {
  return Array.isArray(raw.alocacoes) ? raw.alocacoes.filter(isRecord) : [];
}

function toFornecedorAlocacoes(raw: unknown, index: number): FornecedorAlocacoes | null {
  if (!isRecord(raw)) return null;

  const fornecedor = toFornecedorResumo(raw);
  const id = fornecedor.id || `fornecedor-${index}`;

  return {
    ...fornecedor,
    id,
    alocacoes: readAlocacoes(raw).map((alocacao, position): AlocacaoDoFornecedor => ({
      id: readId(alocacao) || `${id}-${position}`,
      inicio: readDate(alocacao, "inicio"),
      fim: readDate(alocacao, "fim"),
      obra: toObraResumo(alocacao.obra),
    })),
    conflitosIgnorados: readList(raw, "conflitosIgnorados")
      .map(toConflitoIgnorado)
      .filter((item): item is FornecedorConflitoIgnorado => item !== null),
  };
}

function toObraAlocacoes(raw: unknown, index: number): ObraAlocacoes | null {
  if (!isRecord(raw)) return null;

  const obra = toObraResumo(raw);
  const id = obra.id || `obra-${index}`;

  return {
    ...obra,
    id,
    alocacoes: readAlocacoes(raw).map((alocacao, position): AlocacaoDaObra => ({
      id: readId(alocacao) || `${id}-${position}`,
      inicio: readDate(alocacao, "inicio"),
      fim: readDate(alocacao, "fim"),
      fornecedor: toFornecedorResumo(alocacao.fornecedor),
    })),
  };
}

function buildParams({ dataInicio, dataFim, busca }: ListAlocacoesParams) {
  const params: Record<string, HttpQueryParam> = { dataInicio, dataFim };
  const term = busca?.trim().slice(0, BUSCA_MAX_LENGTH);
  if (term) params.busca = term;
  return params;
}

/** Carga de trabalho agrupada por fornecedor ("Visualizar por: Fornecedor"). */
export async function listAlocacoesPorFornecedor(
  request: ListAlocacoesParams,
): Promise<FornecedorAlocacoes[]> {
  const response = await getHttpClient().get<unknown>(RECURSOS_PATHS.ALOCACOES_POR_FORNECEDOR, {
    params: buildParams(request),
    signal: request.signal,
  });

  return extractItems(response.data, ["fornecedores"])
    .map(toFornecedorAlocacoes)
    .filter((item): item is FornecedorAlocacoes => item !== null);
}

/** Carga de trabalho agrupada por obra ("Visualizar por: Obra"). */
export async function listAlocacoesPorObra(request: ListAlocacoesParams): Promise<ObraAlocacoes[]> {
  const response = await getHttpClient().get<unknown>(RECURSOS_PATHS.ALOCACOES_POR_OBRA, {
    params: buildParams(request),
    signal: request.signal,
  });

  return extractItems(response.data, ["obras"])
    .map(toObraAlocacoes)
    .filter((item): item is ObraAlocacoes => item !== null);
}

function buildExportParams({
  dataInicio,
  dataFim,
  busca,
  filtros,
  format,
}: ExportAlocacoesFornecedorParams) {
  const params: Record<string, HttpQueryParam> = { dataInicio, dataFim, formato: format };
  const term = busca?.trim().slice(0, BUSCA_MAX_LENGTH);
  if (term) params.busca = term;
  appendReportQuery(params, filtros);
  return params;
}

/** Exportar Visualização — alocações agrupadas por fornecedor (US-2392). */
export async function exportAlocacoesPorFornecedor(
  request: ExportAlocacoesFornecedorParams,
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    `${RECURSOS_PATHS.ALOCACOES_POR_FORNECEDOR}/exportacao`,
    { params: buildExportParams(request), signal: request.signal, responseType: "arraybuffer" },
  );
  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** Exportar Visualização — alocações agrupadas por obra/fornecedor (US-2392). */
export async function exportAlocacoesFornecedorPorObra(
  request: ExportAlocacoesFornecedorParams,
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    `${RECURSOS_PATHS.ALOCACOES_POR_OBRA}/exportacao`,
    { params: buildExportParams(request), signal: request.signal, responseType: "arraybuffer" },
  );
  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}
